import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Phone, Send, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const projects = [
  {
    title: "HELENE: Ignoring The Red Tape",
    description: "This film dives deep into the aftermath of one of the most catastrophic storms to hit the region.",
    youtubeId: "_9qSuF98nm8",
    youtubeUrl: "https://www.youtube.com/watch?v=_9qSuF98nm8&t=647s",
  },
  {
    title: "Senior Citizen Trafficking Is Real",
    description: "This documentary uncovers a side of trafficking most people don't even know exists.",
    youtubeId: "ZlidR_eW7Go",
    youtubeUrl: "https://www.youtube.com/watch?v=ZlidR_eW7Go&t=414s",
  },
  {
    title: "COSMO CAN'T DIE",
    description: "This documentary follows his fight, his family's strength, and the relentless will to rise.",
    image: "/cosmo-photo.png",
    youtubeId: null,
    youtubeUrl: null,
    comingSoon: true,
  },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({ email: "", first_name: "", last_name: "", phone: "" });
  const [contactData, setContactData] = useState({ name: "", email: "", message: "" });
  const [subLoading, setSubLoading] = useState(false);
  const [conLoading, setConLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!formData.email) return;
    setSubLoading(true);
    try {
      await axios.post(`${API}/subscribers`, formData);
      toast.success("You're subscribed! We'll keep you updated.");
      setFormData({ email: "", first_name: "", last_name: "", phone: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubLoading(false);
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactData.name || !contactData.email || !contactData.message) return;
    setConLoading(true);
    try {
      await axios.post(`${API}/contact`, contactData);
      toast.success("Message sent! We'll get back to you soon.");
      setContactData({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setConLoading(false);
    }
  };

  return (
    <div className="font-body" data-testid="contact-page">
      {/* Newsletter Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.25em] text-[#C86B53] font-semibold mb-4">
              Stay Connected
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-5xl sm:text-6xl font-bold tracking-tight leading-none text-[#292524] mb-6">
              Subscribe to Stay Up to Date!
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E]">
              We will email you about new releases and new crowdfunding opportunities every time we
              have an upcoming project.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Newsletter + Contact Forms */}
      <section className="pb-24 lg:pb-32" data-testid="newsletter-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.form
              onSubmit={handleSubscribe}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white p-8 lg:p-10 rounded-sm border border-[rgba(41,37,36,0.06)]"
              data-testid="subscribe-form"
            >
              <h3 className="font-heading text-2xl font-bold text-[#292524] mb-6">
                Subscribe to Our Newsletter
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#57534E] font-semibold mb-2">
                    First Name
                  </label>
                  <Input
                    type="text"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="h-10 bg-[#FAF9F6] border-[rgba(41,37,36,0.1)] rounded-sm"
                    data-testid="subscribe-first-name"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#57534E] font-semibold mb-2">
                    Last Name
                  </label>
                  <Input
                    type="text"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="h-10 bg-[#FAF9F6] border-[rgba(41,37,36,0.1)] rounded-sm"
                    data-testid="subscribe-last-name"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-[0.15em] text-[#57534E] font-semibold mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-10 bg-[#FAF9F6] border-[rgba(41,37,36,0.1)] rounded-sm"
                  data-testid="subscribe-email"
                />
              </div>
              <div className="mb-6">
                <label className="block text-xs uppercase tracking-[0.15em] text-[#57534E] font-semibold mb-2">
                  Phone
                </label>
                <Input
                  type="tel"
                  placeholder="(000) 000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-10 bg-[#FAF9F6] border-[rgba(41,37,36,0.1)] rounded-sm"
                  data-testid="subscribe-phone"
                />
              </div>
              <Button
                type="submit"
                disabled={subLoading}
                className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm w-full h-11 text-sm font-semibold tracking-wide"
                data-testid="subscribe-submit-button"
              >
                {subLoading ? "Subscribing..." : "Subscribe Now"}
              </Button>
            </motion.form>

            {/* Contact Form */}
            <motion.form
              onSubmit={handleContact}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="bg-white p-8 lg:p-10 rounded-sm border border-[rgba(41,37,36,0.06)]"
              data-testid="contact-form"
            >
              <h3 className="font-heading text-2xl font-bold text-[#292524] mb-6">
                Send Us a Message
              </h3>
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-[0.15em] text-[#57534E] font-semibold mb-2">
                  Your Name *
                </label>
                <Input
                  type="text"
                  placeholder="Full name"
                  value={contactData.name}
                  onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                  required
                  className="h-10 bg-[#FAF9F6] border-[rgba(41,37,36,0.1)] rounded-sm"
                  data-testid="contact-name"
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-[0.15em] text-[#57534E] font-semibold mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={contactData.email}
                  onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                  required
                  className="h-10 bg-[#FAF9F6] border-[rgba(41,37,36,0.1)] rounded-sm"
                  data-testid="contact-email"
                />
              </div>
              <div className="mb-6">
                <label className="block text-xs uppercase tracking-[0.15em] text-[#57534E] font-semibold mb-2">
                  Message *
                </label>
                <textarea
                  placeholder="How can we help?"
                  value={contactData.message}
                  onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                  required
                  rows={4}
                  className="flex w-full rounded-sm border border-[rgba(41,37,36,0.1)] bg-[#FAF9F6] px-3 py-2 text-sm transition-colors placeholder:text-[#57534E]/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#C86B53] font-body resize-none"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={conLoading}
                className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm w-full h-11 text-sm font-semibold tracking-wide"
                data-testid="contact-submit-button"
              >
                <Send size={14} strokeWidth={2} className="mr-1" />
                {conLoading ? "Sending..." : "Send Message"}
              </Button>
            </motion.form>
          </div>

          {/* Donate CTA below forms */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 text-center"
            data-testid="contact-donate-section"
          >
            <p className="font-heading text-2xl font-bold text-[#292524] mb-3">
              Want to make an even bigger impact?
            </p>
            <p className="text-sm font-medium text-[#57534E] mb-6">
              Every dollar goes toward production costs, creator support, and sharing stories that would otherwise remain unseen.
            </p>
            <a
              href="https://www.paypal.com/ncp/payment/3KN4GBXUVPSZL"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-donate-button"
            >
              <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-10 h-12 text-sm font-semibold tracking-wide">
                <Heart size={16} strokeWidth={2} className="mr-2" />
                Donate Now
                <ArrowRight size={16} strokeWidth={2} className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-[#F2EDE4]" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12">
            <a
              href="mailto:thevisible.foundation@gmail.com"
              className="flex items-center gap-2 text-sm font-medium text-[#57534E] hover:text-[#C86B53] transition-colors"
              data-testid="contact-info-email"
            >
              <Mail size={16} strokeWidth={2} />
              thevisible.foundation@gmail.com
            </a>
            <Separator orientation="vertical" className="hidden sm:block h-5 bg-[rgba(41,37,36,0.15)]" />
            <a
              href="tel:734-928-8369"
              className="flex items-center gap-2 text-sm font-medium text-[#57534E] hover:text-[#C86B53] transition-colors"
              data-testid="contact-info-phone"
            >
              <Phone size={16} strokeWidth={2} />
              734-928-8369
            </a>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 lg:py-32" data-testid="contact-projects-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
              Our Work
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#292524] mb-16">
              Our Recent Projects
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((project, i) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
              >
                {project.youtubeId ? (
                  <div className="video-container rounded-sm overflow-hidden shadow-md">
                    <iframe
                      src={`https://www.youtube.com/embed/${project.youtubeId}`}
                      title={project.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                ) : project.image ? (
                  <div className="relative aspect-video rounded-sm overflow-hidden bg-black">
                    <img src={project.image} alt={project.title} className="w-full h-full object-contain" />
                    <div className="absolute top-3 right-3">
                      <span className="bg-[#C86B53] text-white text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm font-semibold">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="relative aspect-video rounded-sm overflow-hidden bg-[#292524] flex items-center justify-center">
                    <div className="text-center px-6">
                      <p className="font-heading text-2xl font-bold text-white mb-2">{project.title}</p>
                      <span className="bg-[#C86B53] text-white text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm font-semibold">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                )}
                <h3 className="font-heading text-xl font-bold text-[#292524] mt-5 mb-2">
                  {project.title}
                </h3>
                <p className="text-sm font-medium text-[#57534E] leading-relaxed mb-4">
                  {project.description}
                </p>
                {project.youtubeUrl ? (
                  <Link
                    to="/films"
                    className="text-sm font-semibold text-[#C86B53] hover:text-[#b55c46] inline-flex items-center gap-1 transition-colors"
                  >
                    Watch Now
                    <ArrowRight size={14} strokeWidth={2} />
                  </Link>
                ) : (
                  <span className="text-sm font-semibold text-[#57534E]">Coming Soon</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
